import {
  Icon,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuNote,
  MenuPopup,
  MenuSeparator,
  type MenuAnchor,
  toastManager,
} from "@salestrak/ui";

import {
  UpdateCycleRows,
  type UpdateCyclePick,
} from "#app/components/updates/update-cycle-rows";
import { isClosedStageType, type StageOption, type UpdateStageInput } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useStageCycleOverrideCount } from "#app/lib/hooks/use-stage-cycle-override-count";
import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { formatUpdateCycle, normalizeUpdateCycle } from "#app/lib/project-updates";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/** Every column of the pin the editor reads plus the workflow rung it names.
 *  `workflowId` rides `StageListItem_stage`, so a board column header — which
 *  is where this opens from — already has it. */
type PinnableStage = StageOption & { workflowId?: string | null };

// The Stage's Admin pin, set from its board column's ⋯ menu or its list
// group's "…" menu — the two places Stage cycles are managed. The menu IS the
// list: one group label names it,
// the rows are the cycle ladder with the Stage's current state checked, and a
// pick commits at once (toast confirms; picking again reverts). Unpinned OPEN
// Stages follow their Workflow's default cycle; picking a cycle (or off) here
// PINS the Stage — the default Projects here inherit in place of the
// Workflow's. Closed Stages wear the SAME list: "Don't require follow ups" opens
// checked (off is their baseline — a lingering off-pin changes nothing) and
// there is no inherit row since closed never falls to the Workflow default;
// picking a cycle is the value-pin opt-in that puts updates on finished work,
// and picking off again clears the pin. Only a default: Projects carrying
// their own cycle keep it, and the per-Project picker (even in an off-pinned
// Stage) stays open for the cycle a user wants. Anchored to the ⋯ button
// itself on the menu's own geometry — side="bottom" align="start", the rung
// the column menu uses — so it lands on the identical top-left corner that
// menu occupied and reads as that menu being replaced in place.
export function StageUpdateCycleMenu({
  open,
  onOpenChange,
  stage,
  anchor,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stage: PinnableStage | null;
  /** The open column's or group's "…" button — a virtual element whose rect is
   *  read on every positioning pass, so the popup opens on the "…" and tracks
   *  the host's scroll. */
  anchor: MenuAnchor;
}) {
  const { updateStage } = useStageMutations();
  const { workflowMap } = useWorkspaceData();
  // Anyone with manage.edit_workflows can pin or un-pin a cycle; pinning a
  // Stage's updates OFF is admin-territory.
  const canTurnOff = usePermission("manage.stage_updates_off");
  // Pinning never touches per-Project overrides — the server-side count
  // tells the admin how many Projects keep their own cycle instead.
  const overrideCount = useStageCycleOverrideCount(stage?.stageId, open);

  if (!stage) return null;

  const storedCycle = normalizeUpdateCycle(stage.updateCycle);
  const pinnedOff = stage.updateCycleOff === true;
  const closed = isClosedStageType(stage.type);
  const workflow = stage.workflowId
    ? workflowMap.get(stage.workflowId)
    : undefined;
  // The checked row reads the level's real state: a closed Stage doesn't ask
  // until a value pin opts it in (a lingering off-pin from its open life
  // changes nothing), an open Stage only when off-pinned.
  const value: UpdateCyclePick = closed
    ? (storedCycle ?? "off")
    : pinnedOff
      ? "off"
      : (storedCycle ?? "inherit");
  // Closed Stages get no inherit row: there's no Workflow rung under them, and
  // the off row owns their baseline. The detail is the Workflow's resolved
  // cycle; a Workflow the map has not loaded gives the row no detail rather
  // than a guess.
  const inheritLabel = closed ? undefined : "Workflow default";
  const inheritDetail =
    closed || !workflow ? undefined : formatUpdateCycle(workflow.updateCycle);
  // An off-pinned Stage keeps the off row checked and live without the
  // permission — picking another row turns updates back ON, which any
  // workflow editor may do. A closed Stage's off row is free both ways:
  // picking it only clears the opt-in pin (back to the closed baseline), so
  // there's no opt-out to gate.
  const offLocked = closed ? false : !canTurnOff && !pinnedOff;
  const overrideNote =
    overrideCount === 1
      ? "1 Project has its own cycle and will keep it."
      : `${overrideCount.toLocaleString()} Projects have their own cycle and will keep them.`;

  const handlePick = async (pick: UpdateCyclePick) => {
    try {
      let input: UpdateStageInput;
      if (pick === "off") {
        // Closed: back to the baseline — clear the pair and any lingering off
        // flag in one write. Never store an off-PIN there: it would ride
        // along as a surprise off if the Stage ever flips back open.
        input = closed
          ? { updateCycle: null, updateCycleOff: false }
          : { updateCycleOff: true };
      } else if (pick === "inherit") {
        // Un-pin: clear the pair AND the off flag in one write (null-and-false
        // passes the server's value+off rejection — only truthy pairs collide).
        input = { updateCycle: null, updateCycleOff: false };
      } else {
        input = { updateCycle: pick };
      }
      const result = await updateStage(stage.stageId, input, { via: "stage_cycle_menu" });
      if (result && "message" in result) {
        toastManager.add({
          title: result.message ?? "Failed to save follow up cycle",
          type: "error",
        });
        return;
      }
    } catch (error) {
      toastMutationError(error, "Failed to save follow up cycle");
    }
  };

  return (
    <Menu open={open} onOpenChange={onOpenChange}>
      {/* Anchored to the ⋯ button rather than a trigger of its own: the menu
          that opened this is already gone by the time it mounts. `align`,
          `side`, the 4px offset and the `panel` width match that menu's, so
          this opens exactly where it was instead of somewhere else in the
          column. No submenus, so the triggerless root is safe. */}
      <MenuPopup align="start" anchor={anchor} width="panel">
        {/* MenuGroupLabel reads its labelled set from MenuGroup context, so the
            label and the rows it names are one group. */}
        <MenuGroup>
          <MenuGroupLabel>Follow up cycle · {stage.name}</MenuGroupLabel>
          <UpdateCycleRows
            value={value}
            onPick={(pick) => void handlePick(pick)}
            storedCycle={storedCycle}
            inheritLabel={inheritLabel}
            inheritDetail={inheritDetail}
            offLocked={offLocked}
          />
        </MenuGroup>
        {overrideCount > 0 ? (
          <>
            <MenuSeparator />
            {/* Informational, whatever gets picked: overrides survive a pin. On
                the rows' grid, so the glyph sits in the check column and the
                sentence starts on the label edge. */}
            <MenuNote icon={<Icon name="info" size="md" color="muted" />}>
              {overrideNote}
            </MenuNote>
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}
